import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import './ActiveTradesPage.css';
import ActiveTradesHeader from './components/ActiveTradesHeader';
import TradeStatusTabs from './components/TradeStatusTabs';
import ShippingGuideBanner from './components/ShippingGuideBanner';
import TradeCard from './components/TradeCard';
import { useGetActiveTradesQuery } from 'store/services/trade/tradeApi';
import { AssetUrls } from 'config/assets';
import MagicButton from 'components/MagicButton/MagicButton';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';
import SearchInput from 'components/SearchInput/SearchInput';
import Pagination from 'components/Pagination/Pagination';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { TRADES_PAGE_SIZE } from 'constants/trades';

export type TradeStatus = 'ready' | 'transit' | 'completed' | 'cancelled';

export interface TradeItem {
  qty: number;
  name: string;
  set: string;
  setCode?: string;
  setIconUrl?: string;
  collectorNumber?: string;
  condition: string;
  language: string;
  image: string;
  scryfallId?: string;
  pointValue?: number;
  foil?: boolean;
  completedAt?: string | null;
}

export interface Trade {
  id: string;
  tradeNumber: string;
  traderId: string;
  traderName: string;
  traderAvatar: string;
  traderIsElite?: boolean;
  traderShowAnimatedBorder?: boolean;
  traderBorderColor1?: string | null;
  traderBorderColor2?: string | null;
  pointBalance?: number;
  initiated: string;
  initiatedRaw?: string;  // Keep the raw ISO date for ShippingCountdown
  shipped?: string;
  completed?: string;
  packingSlipAvailable?: boolean;
  packingSlipExpiresAt?: string | null;
  cancelled?: string;
  estimatedDelivery?: string;
  status: TradeStatus;
  items: TradeItem[];
  totalValue: number;
  pendingValue?: number;
  tradeFee?: number;
  speed: number;
  accuracy: number;
  communication: number;
  shippingAddress: {
    name: string;
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  cancelledRaw?: string;
  isRemovedItem?: boolean;
  removedFromTradeNumber?: string;
  // Trade-offer leg not yet open: waiting for its funding trade to complete.
  // Shipping is blocked until then; the trade number explains what it waits for.
  awaitingFunding?: boolean;
  fundedByTradeNumber?: string;
}

// Format date from ISO string to MM.DD.YYYY
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}.${day}.${year}`;
  } catch {
    return dateString; // Return original string if parsing fails
  }
};

export default function ActiveTradesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TradeStatus>('ready');
  const [retryCount, setRetryCount] = useState(0);
  const [skipQuery, setSkipQuery] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const hasSetInitialTab = useRef(false);

  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  // When the field is cleared (X button), bypass the debounce so one click
  // restores the unfiltered list immediately.
  const effectiveSearch = searchTerm === '' ? '' : debouncedSearch;

  // New search restarts from page 1
  useEffect(() => {
    setPage(1);
  }, [effectiveSearch]);

  // Switching tabs keeps the search query but restarts from page 1
  const handleTabChange = (tab: TradeStatus) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch one page of the active tab's trades from API
  const { data: tradesData, isLoading, isFetching, error, refetch } = useGetActiveTradesQuery(
    {
      type: 'sending',
      status: activeTab,
      page,
      limit: TRADES_PAGE_SIZE,
      search: effectiveSearch.trim() || undefined,
    },
    {
      // Skip the query if we've had too many 401 errors
      skip: skipQuery
    }
  );

  // If the list shrinks under us (e.g. a trade completes and the last page
  // disappears after cache invalidation), fall back to the last valid page.
  useEffect(() => {
    if (tradesData && page > 1 && page > tradesData.pagination.totalPages) {
      setPage(Math.max(1, tradesData.pagination.totalPages));
    }
  }, [tradesData, page]);

  // Subscribe to real-time balance updates for visible traders
  const traderIds = useMemo(
    () => (tradesData?.trades || []).map(t => t.traderId),
    [tradesData?.trades]
  );
  useTraderBalanceSocket({ traderIds, enabled: traderIds.length > 0 });

  // Convert API trades to component format
  const trades: Trade[] = tradesData?.trades.map(trade => ({
    id: trade.id,
    tradeNumber: trade.tradeNumber,
    traderId: trade.traderId.toString(),
    traderName: trade.traderName,
    traderAvatar: trade.traderAvatar || '',
    traderIsElite: trade.traderIsElite,
    traderShowAnimatedBorder: trade.traderShowAnimatedBorder,
    traderBorderColor1: trade.traderBorderColor1,
    traderBorderColor2: trade.traderBorderColor2,
    pointBalance: trade.pointBalance,
    initiated: formatDate(trade.initiated),
    initiatedRaw: trade.initiated,  // Keep raw date for ShippingCountdown
    shipped: trade.shipped ? formatDate(trade.shipped) : undefined,
    completed: trade.completed ? formatDate(trade.completed) : undefined,
    cancelled: trade.cancelled ? formatDate(trade.cancelled) : undefined,
    cancelledRaw: trade.cancelled || undefined,
    estimatedDelivery: trade.estimatedDelivery,
    packingSlipAvailable: trade.packingSlipAvailable,
    packingSlipExpiresAt: trade.packingSlipExpiresAt,
    // pending_funding legs bucket into the ready tab server-side; the card
    // renders their waiting state from awaitingFunding instead of ship actions
    status: trade.status === 'pending_funding' ? 'ready' : trade.status,
    awaitingFunding: trade.awaitingFunding || trade.status === 'pending_funding',
    fundedByTradeNumber: trade.fundedByTradeNumber,
    items: trade.items.map(item => ({
        qty: item.qty,
        name: item.name,
        set: item.set,
        setCode: (item as any).setCode,
        setIconUrl: (item as any).setIconUrl,
        collectorNumber: (item as any).collectorNumber,
        condition: item.condition,
        language: item.language,
        image: item.image,
        scryfallId: item.scryfallId,
        pointValue: item.pointValue || 0,  // Backend should provide the actual point value at trade initiation
        foil: item.foil || false,
        finish: (item as any).finish ?? undefined,
        completedAt: item.completedAt,
    })),
    totalValue: trade.totalValue,
    pendingValue: trade.pendingValue,
    tradeFee: trade.tradeFee,
    speed: 5,  // Rating out of 5 stars
    accuracy: 5,  // Rating out of 5 stars
    communication: 5,  // Rating out of 5 stars
    shippingAddress: trade.shippingAddress ? {
      name: `${trade.shippingAddress.firstName || ''} ${trade.shippingAddress.lastName || ''}`.trim() || 'Not provided',
      street: trade.shippingAddress.address1 || '',
      street2: trade.shippingAddress.address2 || undefined,
      city: trade.shippingAddress.city || '',
      state: trade.shippingAddress.state || '',
      zipCode: trade.shippingAddress.postalCode || '',
      country: trade.shippingAddress.country || '',
    } : {
      name: 'Not provided',
      street: '',
      street2: undefined,
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    isRemovedItem: (trade as any).isRemovedItem || false,
    removedFromTradeNumber: (trade as any).removedFromTradeNumber,
  })) || [];

  // The server returns the active tab's page already filtered, searched and
  // sorted (cancelled interleaves synthetic removed-item entries in SQL).
  const summaryStats = {
    ready: tradesData?.stats.statusCounts.ready ?? 0,
    transit: tradesData?.stats.statusCounts.transit ?? 0,
    completed: tradesData?.stats.statusCounts.completed ?? 0,
    totalPoints: tradesData?.stats.totalPoints ?? 0,
  };

  // Auto-select the first tab with content on initial load
  useEffect(() => {
    if (tradesData && !hasSetInitialTab.current) {
      hasSetInitialTab.current = true;
      if (tradesData.stats.statusCounts.ready === 0 && tradesData.stats.statusCounts.transit > 0) {
        setActiveTab('transit');
      }
    }
  }, [tradesData]);

  if (isLoading) {
    return (
      <div className="active-trades-page">
        <ActiveTradesHeader stats={{ ready: 0, transit: 0, completed: 0, totalPoints: 0 }} />
        <ShippingGuideBanner />
        <TradeStatusTabs activeTab={activeTab} onTabChange={handleTabChange} />
        <SearchInput
          className="trades-search"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search for username or card name"
        />
        <div className="trades-container">
          <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
            Loading trades...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorStatus = (error as any).status;
    const errorMessage = (error as any).data?.message || (error as any).data?.error;

    console.error('Failed to fetch trades:', {
      status: errorStatus,
      message: errorMessage,
      fullError: error
    });

    // Special handling for 401 errors
    if (errorStatus === 401) {
      console.warn('⚠️ 401 error on /sending page - This might be a backend permission issue');

      // Show a user-friendly error message instead of logging out
      return (
        <div className="active-trades-page">
          <ActiveTradesHeader stats={{ ready: 0, transit: 0, completed: 0, totalPoints: 0 }} />
          <ShippingGuideBanner />
          <TradeStatusTabs activeTab={activeTab} onTabChange={handleTabChange} />
          <div className="trades-container">
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#fff',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h3 style={{ color: '#ff9800', marginBottom: '20px' }}>
                Unable to Load Trades
              </h3>
              <p style={{ marginBottom: '20px' }}>
                We're having trouble loading your trades. This sometimes happens with new accounts.
                Your trade has been initiated successfully and will appear here shortly.
              </p>
              <button
                onClick={() => {
                  const newCount = retryCount + 1;
                  setRetryCount(newCount);
                  if (newCount > 2) {
                    setSkipQuery(true);
                  }
                  refetch();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9c27b0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Try Again
              </button>
              <p style={{ marginTop: '20px', fontSize: '14px', color: '#aaa' }}>
                If this persists, try refreshing the page or logging out and back in.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // For other errors, show a generic error message
  }

  return (
    <div className="active-trades-page">
      <ActiveTradesHeader stats={summaryStats} />
      <ShippingGuideBanner />
      <TradeStatusTabs activeTab={activeTab} onTabChange={handleTabChange} />
      <SearchInput
        className="trades-search"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search for username or card name"
      />

      <div
        className={`trades-container${isFetching ? ' trades-container-fetching' : ''}`}
        aria-busy={isFetching}
      >
        {trades.length > 0 ? (
          trades.map(trade => (
            <TradeCard key={trade.id} trade={trade} />
          ))
        ) : effectiveSearch.trim() ? (
          <div className="trades-search-empty">
            No trades match your search
          </div>
        ) : (
          <div className="sending-empty-state">
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="TradeMagic"
              style={{ width: 64, height: 64 }}
            />
            <div className="sending-empty-state-title">
              No outbound trades yet...
            </div>
            <div className="sending-empty-state-message">
              Head to the "Send Cards" page to find packages to send to the community.
            </div>
            <div className="sending-empty-state-button">
              <MagicButton
                label="Send Cards"
                icon={<img src={AssetUrls.icons.misc.sendPackage} alt="" style={{ width: 18, height: 18 }} />}
                color="purple"
                onPress={() => navigate('/sendcards')}
              />
            </div>
          </div>
        )}
        {tradesData && tradesData.pagination.totalPages > 1 && (
          <div className="trades-pagination">
            <Pagination
              currentPage={page}
              totalPages={tradesData.pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}